import React, { useState } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useQuery } from "@apollo/client/react";
import { PROJECT_BY_ID } from "src/constants/graphql/queries";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SvgIcon from "@mui/material/SvgIcon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PlusCircle as PlusCircleIcon } from "react-feather";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { CLIENT_USERS } from "../../../constants/graphql";
import InviteFormModal from "../../../views/users/InviteFormModal";
import EditUserModal from "../../../views/users/EditUserModal";
import RoleField from "../../TableEntities/Fields/RoleField";
import { usePermissions } from "../../../contexts/permissions";
import UsersMobileView from "./UsersMobileView";
import UsersEmptyState from "./UsersEmptyState";

export default function Users({ children, userType }) {
  const { projectId, clientId } = useParams();
  const [open, setOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { can } = usePermissions();
  const canEditUsers = can("users.write");
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const { data: { project } = {}, loading: loadingProject, refetch: refetchProject } = useQuery(
    PROJECT_BY_ID,
    {
      variables: { id: projectId, userType },
      skip: !projectId,
    }
  );
  const { data: { clientUsers } = {}, loading: loadingClient, refetch: refetchClient } = useQuery(
    CLIENT_USERS,
    {
      variables: { clientId },
      skip: !!projectId,
    }
  );
  const allUsers = [...((projectId ? project?.users : clientUsers) || [])].sort((a, b) => 
    (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase())
  );
  
  const users = allUsers.filter(user => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase().trim();
    const userName = (user?.name || '').toLowerCase();
    const userEmail = (user?.email || '').toLowerCase();
    return userName.includes(search) || userEmail.includes(search);
  });
  
  const showCostCenters = !!projectId && !!project?.settings?.cost_center_user_restrictions;

  const handleRefetch = () => {
    if (projectId) {
      refetchProject();
    } else {
      refetchClient();
    }
  };

  const loading = projectId ? loadingProject : loadingClient;
  const isEmpty = users.length === 0;

  const handleToggle = () => {
    setOpen(v => !v);
  };

  const handleInviteUser = () => {
    handleRefetch();
    handleToggle();
  };

  const renderCostCenters = (costCenters) => {
    if (!costCenters || costCenters.length === 0) return "All";
    if (costCenters.length > 3) return `${costCenters.length} cost centers`;
    return costCenters.map((cc, i) => (
      <React.Fragment key={cc.id}>
        {cc.costCenterName}
        {i < costCenters.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        {children}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={2}
          width={{ xs: "100%", sm: "auto" }}
          justifyContent={{ xs: "space-between", sm: "flex-end" }}
        >
          <Tooltip title="Invite User">
            <Button
              variant="contained"
              size="small"
              onClick={handleToggle}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <SvgIcon fontSize="small">
                <PlusCircleIcon />
              </SvgIcon>
            </Button>
          </Tooltip>
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm ? (
                    <IconButton onClick={() => setSearchTerm("")} size="small" edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <SearchIcon fontSize="small" color="action" />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ 
              minWidth: { xs: 200, sm: 250 },
              maxWidth: 300
            }}
          />
        </Box>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box p={2}>
          {isMobile ? (
            isEmpty ? (
              <UsersEmptyState />
            ) : (
              <UsersMobileView
                users={users}
                canEditUsers={canEditUsers}
                onEdit={setEditingUserId}
              />
            )
          ) : isEmpty ? (
            <UsersEmptyState />
          ) : (
            <TableContainer component={Paper}>
              <Table aria-label="users table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">User Name</TableCell>
                    <TableCell align="center">User Email</TableCell>
                    <TableCell align="center">Last Sign In</TableCell>
                    <TableCell align="center">Permission Role</TableCell>
                    {showCostCenters && <TableCell align="center">Cost Centers</TableCell>}
                    {canEditUsers && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(row => (
                    <TableRow
                      key={row.id || row.email || row.name}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      hover
                    >
                      <TableCell component="th" scope="row" align="center">
                        {row.name}
                      </TableCell>
                      <TableCell align="center">{row.email}</TableCell>
                      <TableCell align="center">
                        {row.currentSignInAt
                          ? new Date(row.currentSignInAt).toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                          : "Never"}
                      </TableCell>
                      <TableCell align="center">
                        <RoleField value={row.role} />
                      </TableCell>
                      {showCostCenters && (
                        <TableCell align="center">
                          {renderCostCenters(row.projectCostCenters)}
                        </TableCell>
                      )}
                      {canEditUsers && (
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label="Edit"
                              onClick={() => setEditingUserId(row.id)}
                              size="small"
                            >
                              <EditOutlinedIcon color="action" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {open && (
            <InviteFormModal
              projectId={projectId}
              onSuccess={handleInviteUser}
              onClose={handleToggle}
            />
          )}
          {editingUserId && (
            <EditUserModal
              userId={editingUserId}
              onSuccess={handleRefetch}
              onClose={() => setEditingUserId(null)}
            />
          )}
        </Box>
      )}
    </>
  );
}
